import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Grid, Button } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { InPageBot } from '@leadoo/react-bot';

import { ProductOverview } from './ProductOverview';
import { BodyFeatureCard } from '../Industry/Features/BodyFeatureCard';
import { BodyFeature } from '../Industry/Features/BodyFeature';
import { Testimonials } from '../Testimonials';
import { MobileFeatures } from '../Industry/Features/Mobile/MobileFeatures';
import { SectionBlue } from '../Sections/SectionBlue';
import { CustomerLogos } from '../CustomerLogos';
import { CtaCard } from '../General/CtaCard';
import { LogoLeftCtaCard } from '../General/LogoLeftCtaCard';
import { SectionWaveDefault } from '../Waves/SectionWaveDefault';
import { IntroStats } from '../Home/Intro/IntroStats';
import { SectionWhite } from '../Sections/SectionWhite';
import { Related } from '../Software/Related';

const useStyles = makeStyles((theme) => ({
  testimonialBackground: {
    backgroundSize: 'cover',
    height: '600px',
    width: '99vw',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      width: '100vw',
      height: '500px',
    },
    [theme.breakpoints.down('sm')]: {
      height: '400px',
    },
    [theme.breakpoints.down('xs')]: {
      backgroundPosition: 'left 60px',
    },
  },
  testimonialContainer: {
    marginTop: '-35rem',
    [theme.breakpoints.down('md')]: {
      marginTop: '-30rem',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '-25rem',
    },
  },
  testimonialTitle: {
    color: theme.white,
    fontWeight: 700,
    padding: '2rem 0',
  },
  cards: {
    padding: '4rem 0',
    [theme.breakpoints.down('md')]: {
      padding: '2rem 0',
    },
  },
}));

export const ProductFeatureBody = ({
  body,
  cta,
  logoLeftCta,
  partnerCards,
  home,
  setFeatureModalOpen,
  heroVideo,
  stats,
  statsHeader,
  statDisclaimer,
  noStatsCta,
  related,
  relatedHeader,
  goBackButton,
  goBackButtonLink,
}) => {
  const classes = useStyles();
  const partnerRef = useRef(null);
  const featureRef = useRef(null);

  // Card array that gets modified on click. Set a fresh array to be manipulated on mount.
  const [cardArray, setCardArray] = useState([]);

  useEffect(() => {
    setCardArray(body.featureSection);
  }, [body.featureSection]);

  const setToFirst = (selected) => {
    const filteredArray = cardArray.filter(
      (item) => item._key !== selected._key
    );
    setCardArray([selected, ...filteredArray]);
  };

  // Handles scrolling to selected feature – currently scrolls to the div above the cards on desktop view.
  const handleSelectFeature = async (e, content) => {
    await setSelectedFeature(content._key);
    setToFirst(content);
    featureRef?.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const partnerSectionScroll = () => {
    partnerRef?.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  // State for selected card for features.
  const [selectedFeature, setSelectedFeature] = useState(null);

  useEffect(() => {
    setSelectedFeature(body.featureSection[0]?._key);
  }, [body.featureSection]);

  const med = useMediaQuery('(max-width: 960px)');

  const {
    _rawOverviewContent,
    overviewImage,
    featureSection,
    featureHeader,
    softwareFeatures,
    testimonials,
    customerLogos,
    testimonialHeader,
    homeCta,
    _rawCustomerLogosHeader,
    overviewCta,
    overviewVideo,
    leadoo = {},
  } = body;

  const video = testimonials.length ? testimonials[0]?.videoVariant : null;

  // Determine how many features to show in the first group.
  const firstSet = cardArray.length === 4 ? 2 : 3;
  const lastSet =
    cardArray.length === 4
      ? -2
      : cardArray.length === 6
      ? -3
      : cardArray.length === 7
      ? -4
      : cardArray.length === 5
      ? -2
      : null;

  const longPage = body.featureSection.length > 2;
  const noCards = cardArray.length <= 2 || home;
  const statsSection = (stats && stats.length) || noStatsCta;

  return (
    <>
      {_rawOverviewContent ? (
        <SectionWhite bottom>
          <Container>
            <ProductOverview
              overviewImage={overviewImage}
              overviewBody={_rawOverviewContent}
              overviewCta={overviewCta}
              overviewVideo={overviewVideo}
            />
          </Container>
        </SectionWhite>
      ) : null}

      {statsSection ? (
        <SectionBlue
          // top={!noStatsCta}
          // extraBottom={!noStatsCta}
          additionalTop={!noStatsCta && 80}
          bottom
          additionalBottom={!noStatsCta && 80}
        >
          <IntroStats
            header={statsHeader}
            stats={stats}
            disclaimer={statDisclaimer}
            noStatsCta={noStatsCta}
          />
          <SectionWaveDefault
            flipHorizontal={true}
            flipVertical={true}
            bottom='-1px'
            fillColorShape='#FFF'
          />
        </SectionBlue>
      ) : null}
      <Container>
        {med ? (
          <MobileFeatures
            featureSection={featureSection}
            featureHeader={featureHeader}
            featureRef={featureRef}
            selectedFeature={selectedFeature}
            accentColor='#3FA529'
            featureListCta={softwareFeatures?.featureListCta}
            noOverview={!_rawOverviewContent}
            api={!!partnerCards?.length}
            partnerSectionScroll={partnerSectionScroll}
            setFeatureModalOpen={setFeatureModalOpen}
            noCards={noCards}
          />
        ) : (
          longPage &&
          !noCards && (
            <BodyFeatureCard
              featureHeader={featureHeader}
              featureSection={featureSection}
              selectedFeature={selectedFeature}
              handleClick={handleSelectFeature}
              featureRef={featureRef}
              featureListCta={softwareFeatures?.featureListCta}
              setFeatureModalOpen={setFeatureModalOpen}
              accentColor='#3FA529'
              noOverview={!_rawOverviewContent}
              api={!!partnerCards?.length}
              partnerSectionScroll={partnerSectionScroll}
              heroVideo={heroVideo}
            />
          )
        )}
      </Container>

      <Container>
        <>
          {!med &&
            cardArray.slice(0, firstSet).map((feature, index, array) => (
              <BodyFeature
                feature={feature}
                index={index}
                key={feature._key}
                selectedFeature={selectedFeature}
                accentColor='#0380B6'
                setSelectedFeature={setSelectedFeature}
                firstSetNoOverview={!longPage}
                home={home}
                homeCta={homeCta}
                cardArray={cardArray}
                noCards={noCards}
                // Pass the prop so the last feature in this group removes bottom padding.
                noPaddingBottom={index === array.length - 1}
              />
            ))}
          {cta && !home && cta.ctaHeader ? (
            <CtaCard
              header={cta._rawCtaBlockHeader ? cta._rawCtaBlockHeader : cta.ctaHeader}
              background={cta.ctaBackground}
              subheader={cta.ctaSubheader}
              ctaImage={cta.ctaImage}
              ctaText={cta.ctaText}
              ctaLink={cta.ctaLink}
              internalLink={cta.internalLink}
            />
          ) : logoLeftCta && !home ? (
            <LogoLeftCtaCard
              background={logoLeftCta.ctaBackground}
              header={logoLeftCta._rawCtaHeader}
              subheader={logoLeftCta.ctaSubheader}
              ctaText={logoLeftCta.ctaText}
              ctaLink={logoLeftCta.ctaLink}
              internalLink={logoLeftCta.internalLink}
              ctaLogo={logoLeftCta.ctaLogo}
            />
          ) : homeCta && home && med && homeCta.ctaHeader ? (
            <CtaCard
              header={cta._rawCtaBlockHeader ? cta._rawCtaBlockHeader : cta.ctaHeader}
              background={homeCta.ctaBackground}
              subheader={homeCta.ctaSubheader}
              ctaText={homeCta.ctaText}
              ctaLink={homeCta.ctaLink}
              internalLink={homeCta.internalLink}
            />
          ) : null}
        </>
        {!!leadoo ? (
          <div style={{ paddingTop: '6rem' }}>
            <InPageBot code={leadoo.botId} seamless={leadoo.seamless} />
          </div>
        ) : null}
      </Container>
      {testimonials?.length && !home ? (
        <SectionBlue
          extraTop={!video}
          top={video}
          additionalTop={video && med ? 60 : video ? 80 : null}
          marginBottom='-220px'
        >
          <SectionWaveDefault top='0px' fillColorShape='#FFF' />
          <Testimonials
            testimonials={testimonials}
            header={testimonialHeader}
            background
          />
        </SectionBlue>
      ) : null}

      {Array.isArray(customerLogos) && customerLogos.length > 0 && !home ? (
        <SectionWhite top additionalTop={testimonials?.length && 220} bottom>
          <CustomerLogos
            customerLogosArray={customerLogos}
            customerLogosHeader={_rawCustomerLogosHeader}
          />
        </SectionWhite>
      ) : null}

      {lastSet ? (
        <div className={classes.featureBackground}>
          <Container>
            {!med &&
              cardArray.slice(lastSet).map((feature, index, array) => (
                <BodyFeature
                  feature={feature}
                  index={index}
                  key={feature._key}
                  selectedFeature={selectedFeature}
                  accentColor='#0380B6'
                  setSelectedFeature={setSelectedFeature}
                  cardArray={cardArray}
                  // Pass the prop so the last feature in this group removes bottom padding.
                  noPaddingBottom={
                    index === array.length - 1 && !related?.length
                  }
                />
              ))}
            {related?.length ? (
              <Related related={related} header={relatedHeader} />
            ) : null}
            {!!goBackButton ? (
              <Grid
                container
                justifyContent='center'
                style={{ paddingTop: related.length ? '4rem' : '8rem' }}
              >
                <Link to={`/${goBackButtonLink}`}>
                  <Button color='primary' size='large' variant='contained'>
                    {goBackButton}
                  </Button>
                </Link>
              </Grid>
            ) : null}
          </Container>
        </div>
      ) : null}
      <SectionWhite extraBottom>
        <SectionWaveDefault
          flipHorizontal={false}
          flipVertical={true}
          bottom='0px'
          fillColorShape='#F5F9FF'
          fillColorBorder='#F5F9FF'
        />
      </SectionWhite>
    </>
  );
};
